import { useCallback, useMemo } from 'react';

import { AgGridServerTable } from 'core/components';
import { NotificationPosition, TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams, ISelectionActionProps, ITableActionProps } from 'core/types';
import { corporateCostingProfileService } from 'corporate/services';
import { ICorporateCostingProfileVendorQuote } from 'corporate/types';
import { IPricingProfile } from 'pricing/types';
import { AttachExistingVendorQuotesButton, ImportVendorQuoteButton } from 'quotes/components';

import { getColumnDef } from './columns';
import { DetachVendorQuotesButton } from './DetachVendorQuotesButton';

interface IProps {
  pricingProfile: IPricingProfile;
}

export const VendorQuoteList = ({ pricingProfile }: IProps) => {
  const columnDefs = useMemo(() => getColumnDef(), []);

  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => [
      <AttachExistingVendorQuotesButton
        key="attachVendorQuotes"
        pricingProfile={pricingProfile}
        onAttachVendorQuoteSuccess={() => {
          gridApi?.refreshServerSide();
        }}
      />,
      <ImportVendorQuoteButton
        key="importVendorQuote"
        pricingProfile={pricingProfile}
        notificationPosition={NotificationPosition.TOP}
        onImportComplete={() => {
          gridApi?.refreshServerSide();
        }}
      />,
    ],
    [pricingProfile]
  );

  const selectionActionButtons = useCallback(
    ({ gridApi, selectedRows, deselectAllRows }: ISelectionActionProps) => {
      return [
        <DetachVendorQuotesButton
          key="detachSelectedVendorQuotesButton"
          pricingProfile={pricingProfile}
          vendorQuotes={selectedRows as ICorporateCostingProfileVendorQuote[]}
          onDetachSuccess={() => {
            deselectAllRows();
            gridApi?.refreshServerSide();
          }}
        />,
      ];
    },
    [pricingProfile]
  );

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      return corporateCostingProfileService.getVendorQuotes({
        filters: {
          ...filters,
          pricingProfileId: pricingProfile.id,
        },
        ...rest,
      });
    },
    [pricingProfile.id]
  );

  return (
    <SelectionCountProvider>
      <AgGridServerTableProvider
        tableId="costingProfileVendorQuotesTable"
        entityType="VendorQuote"
        columnDefs={columnDefs}
        getRows={getRows}
        hasLayoutActions
        hasManageFilters
        layoutType={TableLayoutType.COSTING_PROFILE_VENDOR_QUOTES}
      >
        <AgGridServerTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
        />
      </AgGridServerTableProvider>
    </SelectionCountProvider>
  );
};
